import { styled } from '@mui/material/styles';
import MuiAccordion, { AccordionProps } from '@mui/material/Accordion';
import MuiAccordionSummary, { AccordionSummaryProps } from '@mui/material/AccordionSummary';
import MuiAccordionDetails from '@mui/material/AccordionDetails';
import CustomText from 'components/common/CustomText/CustomText';
import { Box, IconButton } from '@mui/material';
import { colors } from 'styles/colors';
import { hexWithOpacity } from 'common/utils';
import useGlobalStyles from 'styles/globalStyles';
import SettingsIcon from '@mui/icons-material/Settings';
import EditIcon from 'icons/EditIcon';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { EPermission } from 'enum/EPermission';
import { useUpdatePermission } from 'hooks/useUsers';
import { snackbarService } from 'services/snackbarService';
import { useUsersContext } from 'context/UsersContext';
import { useAuth } from 'context/AuthContext';

const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(({ theme }) => ({
  padding: 0,
  '&:not(:last-child)': {
    borderBottom: 0,
  },
  '&::before': {
    display: 'none',
  },
}));

const AccordionSummary = styled((props: AccordionSummaryProps) => (
  <MuiAccordionSummary
    expandIcon={<Box sx={{
      width: 25,
      display: 'flex',
      justifyContent: 'center',
      alignItems: 'center',
    }}>
      <SettingsIcon sx={{ fontSize: 22 }} />
    </Box>}
    {...props}
  />
))(({ theme }) => ({
  flexDirection: 'row-reverse',
  padding: '0 0 0 8px',
  borderRadius: '10px',

  '&:hover': {
    backgroundColor: hexWithOpacity(colors.black, 0.04),
  },

  /* kill ALL animations and transitions */
  '& .MuiAccordionSummary-expandIconWrapper': {
    transition: 'none',
  },

  '& .MuiAccordionSummary-expandIconWrapper.Mui-expanded': {
    transform: 'none',
  },


  '& .MuiAccordionSummary-content': {
    marginLeft: '5px',
  },

  ...theme.applyStyles('dark', {
    backgroundColor: 'rgba(255, 255, 255, .05)',
  }),
}));

const AccordionDetails = styled(MuiAccordionDetails)(({ theme }) => ({
  padding: theme.spacing(0),
}));

interface Props {
  doctorId: number;
  onClose: () => void;
}

export default function ChangePermissionAccordion({ doctorId, onClose }: Props) {
  const globalStyles = useGlobalStyles();
  const { refetch } = useUsersContext();
  const { user } = useAuth();

  const { mutate: updateMutation, isLoading } = useUpdatePermission(
    (data: any) => {
      snackbarService.showSnackbar('Permission has been updated successfully', 'success');
      refetch();
    },
    (err: any) => {
      snackbarService.showSnackbar(err, 'error');
    }
  )

  const updatePermission = (permission: EPermission) => {
    onClose();
    updateMutation({
      userId: user.id,
      dto: {
        end_user_id: doctorId,
        permission,
      }
    })
  }

  return (
    <div>
      <Accordion defaultExpanded={false} elevation={0}>
        <AccordionSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
        >
          <CustomText text="Change permission" color={colors.black} />
        </AccordionSummary>

        <AccordionDetails sx={{ marginLeft: 1 }}>

          <IconButton className={globalStyles.popoverButtonContainer} onClick={() => updatePermission(EPermission.EDITOR)}>
            <Box className={globalStyles.popoverIconContainer}>
              <EditIcon size={16} />
            </Box>
            <CustomText text="Editor" ml="5px" color={colors.black} />
          </IconButton>

          <IconButton className={globalStyles.popoverButtonContainer} onClick={() => updatePermission(EPermission.VIEWER)}>
            <Box className={globalStyles.popoverIconContainer}>
              <RemoveRedEyeIcon sx={{ fontSize: 20 }} />
            </Box>
            <CustomText text="Viewer" ml="5px" color={colors.black} />
          </IconButton>

        </AccordionDetails>
      </Accordion>
    </div>
  );
}
